import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Phone, Mail, User } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';
import { useToast } from '@/hooks/use-toast';

type WidgetMode = 'closed' | 'menu' | 'message' | 'callback';

export function FloatingContactWidget() {
  const [mode, setMode] = useState<WidgetMode>('closed');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmitMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });
    
    setIsSubmitting(false);
    setMode('closed');
  };

  const handleSubmitCallback = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: "Callback Requested!",
      description: "Our team will call you shortly.",
    });
    
    setIsSubmitting(false);
    setMode('closed');
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-auto sm:right-6 z-50 flex justify-end">
      <AnimatePresence mode="wait">
        {mode === 'closed' && (
          <motion.button
            key="trigger"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setMode('menu')}
            className="relative w-14 h-14 rounded-full bg-linear-to-br from-orange-600 to-accent text-white shadow-lg shadow-primary/30 flex items-center justify-center group"
          >
            <MessageCircle className="w-6 h-6" />
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full border-2 border-background flex items-center justify-center">
              <span className="w-2 h-2 bg-background rounded-full" />
            </span>
          </motion.button>
        )}

        {mode === 'menu' && (
          <motion.div
            key="menu"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            className="glass-effect rounded-2xl p-4 shadow-xl border border-border/50 w-[min(calc(100vw-2rem),260px)] sm:min-w-50 sm:w-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-primary">Contact Us</h3>
              <button
                onClick={() => setMode('closed')}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            
            <div className="space-y-2">
              <motion.button
                whileHover={{ x: 4 }}
                onClick={() => setMode('message')}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">Send Message</p>
                  <p className="text-xs text-muted-foreground">We reply within 24h</p>
                </div>
              </motion.button>
              
              <motion.button
                whileHover={{ x: 4 }}
                onClick={() => setMode('callback')}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Phone className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">Request Callback</p>
                  <p className="text-xs text-muted-foreground">Get a call back ASAP</p>
                </div>
              </motion.button>
            </div>
          </motion.div>
        )}

        {mode === 'message' && (
          <motion.div
            key="message"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            className="glass-effect rounded-2xl p-4 shadow-xl border border-border/50 w-[min(calc(100vw-2rem),320px)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMode('menu')}
                  className="p-1 rounded-full hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground rotate-45" />
                </button>
                <h3 className="font-semibold text-primary">Send a Message</h3>
              </div>
              <button
                onClick={() => setMode('closed')}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitMessage} className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Your Name"
                  required
                  className="pl-10 bg-background/50 border-border/50 focus:border-primary"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Your Email"
                  required
                  className="pl-10 bg-background/50 border-border/50 focus:border-primary"
                />
              </div>
              <Textarea
                placeholder="How can we help you?"
                required
                rows={3}
                className="bg-background/50 border-border/50 focus:border-primary resize-none"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-linear-to-r from-orange-600 to-accent text-white hover:opacity-90 transition-opacity"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full"
                  />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        )}

        {mode === 'callback' && (
          <motion.div
            key="callback"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            className="glass-effect rounded-2xl p-4 shadow-xl border border-border/50 w-[min(calc(100vw-2rem),320px)]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMode('menu')}
                  className="p-1 rounded-full hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground rotate-45" />
                </button>
                <h3 className="font-semibold text-primary">Request Callback</h3>
              </div>
              <button
                onClick={() => setMode('closed')}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitCallback} className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Your Name"
                  required
                  className="pl-10 bg-background/50 border-border/50 focus:border-primary"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="tel"
                  placeholder="Your Phone Number"
                  required
                  className="pl-10 bg-background/50 border-border/50 focus:border-primary"
                />
              </div>
              <div className="p-3 rounded-xl bg-accent/10 border border-accent/20">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-accent">Available Hours:</span><br />
                  Mon - Fri<br />
                  9:00 AM - 5:00 PM<br />
                  Saturday<br />
                  9:00 AM - 1:00 PM
                </p>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-linear-to-r from-accent to-primary hover:opacity-90 transition-opacity"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full"
                  />
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-2" />
                    Request Callback
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
